export type ErrorReason =
  | { error_type: "unknown" }
  | { error_type: "file_missing" }
  | { error_type: "content_type_not_supported" }
  // The account has visitor attachments switched off (LAS-3469); a widget
  // whose payload predates the switch shows the generic upload error.
  | { error_type: "attachments_disabled" }
  | { error_type: "file_size_too_large"; metadata: { max: string } };

export type FileUploadResult =
  | { success: true; id: string }
  | { success: false; errorReason?: ErrorReason | null };

export interface FileUploadController {
  promise: Promise<FileUploadResult>;
  abort: () => void;
}

export const uploadFile = (
  apiUrl: string,
  token: string,
  file: File,
  onProgress: (_progress: number) => void,
): FileUploadController => {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<FileUploadResult>(resolve => {
    const formData = new FormData();
    formData.append("attachment[file]", file);

    xhr.open("POST", `${apiUrl}/file_uploads`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    // Defensive ceiling — without this, a stalled connection leaves the
    // promise (and the caller's upload UI) pending forever. 5 min is high
    // enough to accommodate large legitimate uploads on slow connections but
    // low enough that an actually-stuck request settles eventually.
    xhr.timeout = 300_000;

    xhr.upload.onprogress = (event): void => {
      if (event.lengthComputable) {
        const percentCompleted = (event.loaded / event.total) * 100;
        onProgress(percentCompleted);
      }
    };

    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText) as { id: string };
          resolve({ success: true, id: response.id });
        } catch {
          // Malformed success body — surface as a generic failure so the
          // upload promise resolves instead of hanging the caller.
          resolve({ success: false, errorReason: { error_type: "unknown" } });
        }
        return;
      }
      if (xhr.status === 422) {
        try {
          const errorReason = JSON.parse(xhr.responseText) as ErrorReason;
          resolve({ success: false, errorReason });
          return;
        } catch {
          // Fall through to the generic-unknown path.
        }
      }
      resolve({
        success: false,
        errorReason: { error_type: "unknown" },
      });
    };

    xhr.onerror = (): void => {
      resolve({ success: false, errorReason: { error_type: "unknown" } });
    };

    xhr.ontimeout = (): void => {
      resolve({ success: false, errorReason: { error_type: "unknown" } });
    };

    xhr.onabort = (): void => {
      resolve({ success: false });
    };

    xhr.send(formData);
  });

  return {
    promise,
    abort: () => xhr.abort(),
  };
};
